import { Flex, Loader, Text, Tooltip } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import cn from 'classnames';
import * as React from 'react';

import { useIsTextTruncated } from '@/hooks/useIsTextTruncated';
import { stopPropagation } from '@/utils/stopPropagation';

import Empty from './Empty';

import s from './TreeNode.module.scss';

export type TreeNodeProps = {
  className?: string;
  headerClassName?: string;
  actions?: React.ReactNode;
  isActive?: boolean;
  isOpen: boolean;
  withArrow?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  title: React.ReactNode;
  /** Кастомный контент тултипа документа (по ховеру на desktop и long-press на тач-устройствах) */
  tooltip?: React.ReactNode;
  children?: React.ReactNode;
  level: number;
  icon?: React.ReactNode;
  isDocument?: boolean;
  /** Документ на проверке ИБ / в обработке — приглушённый вид */
  isContentLocked?: boolean;
};

const TreeNode: React.FC<TreeNodeProps> = ({
  title,
  tooltip,
  className,
  headerClassName,
  withArrow = true,
  isActive = false,
  actions,
  isOpen,
  onClick,
  children,
  isLoading,
  icon,
  level,
  isDocument = false,
  isContentLocked = false,
}) => {
  const [titleRef, isTextTruncated] = useIsTextTruncated('width');
  const touchStartTimeRef = React.useRef<number>(0);

  // Кастомный тултип (название + имя файла) показываем всегда; строковый — только при обрезке текста.
  const hasCustomTooltip = tooltip != null && isDocument;
  const hasStringTooltip = tooltip == null && isDocument && isTextTruncated && typeof title === 'string';
  const showTooltip = hasCustomTooltip || hasStringTooltip;
  const tooltipLabel = hasCustomTooltip ? tooltip : title;

  const handleTouchStart = React.useCallback(() => {
    if (!showTooltip) {
      return;
    }
    touchStartTimeRef.current = Date.now();
  }, [showTooltip]);

  const handleClick = React.useCallback(
    (e: React.BaseSyntheticEvent) => {
      const isLongPress = showTooltip && touchStartTimeRef.current && Date.now() - touchStartTimeRef.current > 500;

      if (isLongPress) {
        e.preventDefault();
        e.stopPropagation();
        touchStartTimeRef.current = 0;

        return;
      }

      touchStartTimeRef.current = 0;
      e.stopPropagation();
      onClick?.();
    },
    [onClick, showTooltip],
  );

  const titleNode =
    typeof title === 'string' ? (
      <Text ref={titleRef} className={cn(s.root__title, isOpen && s.root__title_open)}>
        {title}
      </Text>
    ) : (
      <div ref={titleRef} className={cn(s.root__title_slot, isOpen && s.root__title_open)}>
        {title}
      </div>
    );

  return (
    <Flex
      style={{ '--level': level } as React.CSSProperties}
      className={cn(
        s.root,
        isOpen && s.root_open,
        isActive && s.root_active,
        isContentLocked && s.root_frozen,
        className,
      )}
      direction="column"
    >
      <Flex
        className={cn(s.root__header, headerClassName)}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        gap={8}
        align="center"
      >
        {!isLoading && (
          <IconChevronRight
            size={16}
            className={cn(s.root__arrow, isOpen && s.root__arrow_open, !withArrow && s.root__arrow_hidden)}
          />
        )}
        {isLoading && <Loader className={s.root__loader} />}
        {icon}
        {showTooltip ? (
          <Tooltip
            label={tooltipLabel}
            openDelay={hasCustomTooltip ? 400 : 1000}
            maw={320}
            multiline
            withArrow
            position="top"
            events={{ hover: true, focus: false, touch: true }}
          >
            {titleNode}
          </Tooltip>
        ) : (
          titleNode
        )}
        {actions && (
          <Flex gap={8} align="center" className={s.root__actions} onClick={stopPropagation}>
            {actions}
          </Flex>
        )}
      </Flex>
      {isOpen && <div className={s.root__children}>{children}</div>}
    </Flex>
  );
};

export default Object.assign(TreeNode, {
  Empty,
});
